import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, MessageCircle } from "lucide-react";

const FLEXSHADE_IMG =
    "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/4e8ecf31140220c752c3e0140dffca4e30655ddd7b30a018f33c640a6a019a2e.jpeg";

const SKYCOVER_IMG =
    "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/12a07eb24bb6164b56378492778127f224ab18c74c5ee91a92015a37d2ec0387.jpeg";

const WA =
    "https://wa.me/528116289267?text=" +
    encodeURIComponent(
        "Hola, no sé qué sistema necesito (FlexShade o Skycover). Me podrían orientar?",
    );

export default function ProductSelector() {
    const navigate = useNavigate();

    return (
        <section
            id="productos"
            data-testid="product-selector"
            className="py-20 sm:py-28 bg-white"
        >
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight mb-4">
                        ¿Qué tipo de proyecto tienes?
                    </h2>
                    <p className="text-base sm:text-lg text-eclipse-muted leading-relaxed">
                        Elige el sistema que mejor se adapta a tu espacio.
                        Tenemos dos líneas con propósitos distintos.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* CARD FLEXSHADE */}
                    <article
                        data-testid="product-card-flexshade"
                        className="group bg-white border border-neutral-200 hover:border-eclipse-cta transition-all rounded-sm overflow-hidden shadow-sm hover:shadow-2xl flex flex-col"
                    >
                        <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
                            <img
                                src={FLEXSHADE_IMG}
                                alt="FlexShade — Sistema modular"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute top-4 left-4">
                                <span className="bg-eclipse-cta text-white font-display font-bold text-xs tracking-[0.2em] px-3 py-1.5">
                                    MODULAR
                                </span>
                            </div>
                        </div>
                        <div className="p-7 sm:p-10 flex-1 flex flex-col">
                            <h3 className="font-display font-black text-3xl text-eclipse-primary mb-3">
                                FlexShade
                            </h3>
                            <p className="text-eclipse-muted text-sm sm:text-base leading-relaxed mb-6">
                                Sistema modular prefabricado. Sin permiso.
                                Sin soldadura. Instalación en días. Ideal para
                                estacionamientos, colegios y terrazas.
                            </p>
                            <ul className="space-y-2.5 mb-8">
                                {[
                                    "100% atornillable, sin soldadura",
                                    "Desde 1 cajón",
                                    "20 años de vida útil",
                                ].map((s) => (
                                    <li
                                        key={s}
                                        className="flex items-start gap-2.5 text-sm text-eclipse-primary"
                                    >
                                        <Check
                                            className="w-5 h-5 text-eclipse-cta flex-shrink-0 mt-0.5"
                                            strokeWidth={2.5}
                                        />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid="product-card-flexshade-cta"
                                onClick={() => navigate("/flexshade")}
                                className="btn-eclipse-cta inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm text-sm uppercase tracking-wider mt-auto"
                            >
                                Ver FlexShade
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </article>

                    {/* CARD SKYCOVER */}
                    <article
                        data-testid="product-card-skycover"
                        className="group bg-white border border-neutral-200 hover:border-eclipse-primary transition-all rounded-sm overflow-hidden shadow-sm hover:shadow-2xl flex flex-col"
                    >
                        <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
                            <img
                                src={SKYCOVER_IMG}
                                alt="Skycover — Velarias premium"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute top-4 left-4">
                                <span className="bg-eclipse-primary text-white font-display font-bold text-xs tracking-[0.2em] px-3 py-1.5">
                                    PREMIUM
                                </span>
                            </div>
                        </div>
                        <div className="p-7 sm:p-10 flex-1 flex flex-col">
                            <h3 className="font-display font-black text-3xl text-eclipse-primary mb-3">
                                Skycover
                            </h3>
                            <p className="text-eclipse-muted text-sm sm:text-base leading-relaxed mb-6">
                                Velarias y tensoestructuras arquitectónicas a
                                la medida. Ingeniería de detalle incluida.
                                Desde 100 m². Cobertura nacional.
                            </p>
                            <ul className="space-y-2.5 mb-8">
                                {[
                                    "Membrana PVC hasta 30 años",
                                    "Simulación túnel de viento",
                                    "Premio IFAI 2021",
                                ].map((s) => (
                                    <li
                                        key={s}
                                        className="flex items-start gap-2.5 text-sm text-eclipse-primary"
                                    >
                                        <Check
                                            className="w-5 h-5 text-eclipse-cta flex-shrink-0 mt-0.5"
                                            strokeWidth={2.5}
                                        />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid="product-card-skycover-cta"
                                onClick={() => navigate("/velarias")}
                                className="btn-eclipse-outline-dark inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm text-sm uppercase tracking-wider mt-auto"
                            >
                                Ver Skycover
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </article>
                </div>

                <p className="text-center text-eclipse-muted text-sm mt-10">
                    ¿No sabes cuál necesitas?{" "}
                    <a
                        href={WA}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="product-help-whatsapp"
                        className="text-eclipse-cta hover:underline inline-flex items-center gap-1 font-medium"
                    >
                        <MessageCircle className="w-4 h-4" />
                        Escríbenos
                    </a>
                </p>
            </div>
        </section>
    );
}
